import { Component, computed, effect, inject, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { ServicesProceduresDetailPageStore } from './services-procedures-detail-page.store';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import { fb, routeParams, router } from '../../../../../shared/shared.global-service';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { ActivatedRoute } from '@angular/router';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { contentGlobalSuccess } from '../../../../content.error';
import {
    ServiceMedicalProcedureObject,
} from '@libs/types/src/services/object-type/service/service-medical-procedure.object';
import { getHistoryFrag, serviceMedicalProcedureGridItemFrag } from './services-procedures-detail-page.query';
import { TableColumn, TableColumnType } from '../../../../../shared/components/kit/table/table.types';
import { InputWysiwygComponent } from '../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { CardComponent } from '../../../../../../components/kit/card/card.component';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@openng/optimus-ui/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateFieldPipe } from '../../../../../shared/pipes/translateField.pipe';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { NumberInputComponent } from '../../../../../../components/kit/input/text-input/number-input.component';
import { ColorInputComponent } from '../../../../../../components/kit/input/color-input/color-input.component';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';

@Component({
    selector: 'app-services-procedures-detail-page',
    templateUrl: 'services-procedures-detail-page.component.html',
    styleUrls: ['services-procedures-detail-page.component.scss'],
    providers: [ServicesProceduresDetailPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardComponent,
        Tabs,
        TabList,
        Tab,
        TranslatePipe,
        TabPanels,
        TabPanel,
        TranslateFieldPipe,
        TextInputComponent,
        NumberInputComponent,
        InputWysiwygComponent,
        ColorInputComponent,
        TableComponent,
        ActionButtonsComponent,
    ],
})
export class ServicesProceduresDetailPageComponent extends SubscriptionComponent {

    public readonly store = inject(ServicesProceduresDetailPageStore);
    private readonly route = inject(ActivatedRoute);


    public readonly wysiwygs = viewChildren<InputWysiwygComponent>(InputWysiwygComponent);

    protected form = fb.group({
        customCode: fb.control<string | null>(null),
        customName: fb.control<string | null>(null),
        frequency: fb.control<string | null>(null),
        executionTolerance: fb.control<number | null>(null),
        color: fb.control<string | null>(null),
        textTemplate: fb.control<string>(''),
        courseDescription: fb.control<string>(''),
    });

    protected selectedTab = 'procedures';
    protected prefix = 'service.settings.general.medicalProcedure.';

    protected historyColumns: TableColumn[] = [
        { name: 'dateTo', type: TableColumnType.Date, filterable: false },
        { name: 'version' },
        { name: 'expertiseCode' },
        { name: 'limitationOfPaymentByLocation' },
        { name: 'description', type: TableColumnType.String50 },
        { name: 'explanation', type: TableColumnType.String50 },
        { name: 'paymentCategory' },
        { name: 'description' },
        { name: 'duration' },
        { name: 'durationOfProcedureBearer' },
        { name: 'points' },
    ];

    protected additionalFilter = computed(() => {
        return [[{
            column: 'code',
            operator: 'EQ',
            value: [this.store.data.getServiceMedicalProcedures()?.items?.[0]?.code || ''],
        }]];
    });

    constructor() {
        super();
        effect(async () => {
            const res = await this.store.api.getServiceMedicalProcedures(FilterBuilder.filter({
                id: routeParams.serviceMedicalProcedureId(),
            }), serviceMedicalProcedureGridItemFrag);
            const procedure: ServiceMedicalProcedureObject = res?.items?.[0];
            if (!procedure) return;

            this.form.patchValue({
                ...procedure,
                textTemplate: procedure.textTemplate || '',
                courseDescription: procedure.courseDescription || '',
            });
        });

        this.store.onSuccess(
            () => {
            },
            'updateServiceMedicalProcedure',
        );
    }

    protected async submit(): Promise<void> {
        if (isInvalid(this.form)) return;

        this.form.markAsUntouched();
        const procedure = this.store.procedure();
        if (!procedure) return;

        await this.store.api.updateServiceMedicalProcedure({
            id: procedure.id,
            ...this.form.value,
        });
        this.wysiwygs()?.forEach((it) => it.resetBackupValues());

        contentGlobalSuccess.next('common.updated');
        await router.navigate(['../'], { relativeTo: this.route });
    }

    protected readonly getHistoryFrag = getHistoryFrag;

}
